import { useCallback, useState } from "react";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";
import useWindowedMasonry from "hooks/useWindowedMasonry";
import CommunityCard, { estimateCardHeight } from "./CommunityCard";
import PostDialog from "./PostDialog";
import ProfileDialog from "./ProfileDialog";
import ImageLightbox from "./ImageLightbox";

const GAP = 16;

// Layout shell only — visual card styling lives on CommunityCard (theme-aware).
const cardShellSx = {
  borderRadius: 4,
  bgcolor: "transparent",
  boxShadow: "none",
};

export const getFeedKey = (item) => item?._feedKey || item?.id || item?.thread_id;

function FeedStatus({ loading, error, hasMore, onRetry, count }) {
  if (loading) {
    return (
      <Stack direction="row" spacing={1.2} alignItems="center" justifyContent="center" py={3}>
        <CircularProgress size={20} sx={{ color: "#0061FE" }} />
        <Typography variant="caption" color="text.secondary">
          Loading more posts…
        </Typography>
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack alignItems="center" spacing={1} py={3}>
        <Typography variant="caption" color="text.secondary">
          Couldn&apos;t load more posts.
        </Typography>
        <MDButton size="small" variant="outlined" color="info" onClick={onRetry}>
          Try again
        </MDButton>
      </Stack>
    );
  }
  if (!hasMore && count > 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={1.5} py={3} px={2}>
        <Box
          flex={1}
          height="1px"
          sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(148,163,184,0.2)" : "rgba(15, 23, 42, 0.08)") }}
        />
        <Typography variant="caption" color="text.secondary">
          You&apos;re all caught up
        </Typography>
        <Box
          flex={1}
          height="1px"
          sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(148,163,184,0.2)" : "rgba(15, 23, 42, 0.08)") }}
        />
      </Stack>
    );
  }
  return null;
}

FeedStatus.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.any,
  hasMore: PropTypes.bool,
  onRetry: PropTypes.func,
  count: PropTypes.number,
};

/** Windowed masonry feed: only cards near the viewport are mounted. */
function CommunityFeed({
  items,
  columnCount,
  loading,
  error,
  hasMore,
  sentinelRef,
  onRetry,
}) {
  const { containerRef, containerStyle, visible, measureRef, getItemStyle } = useWindowedMasonry({
    items,
    getKey: getFeedKey,
    columnCount,
    gap: GAP,
    estimateHeight: estimateCardHeight,
  });

  // Overlays live here (not in cards) so they survive cards being windowed out.
  const [postItem, setPostItem] = useState(null);
  const [focusComposer, setFocusComposer] = useState(false);
  const [profileItem, setProfileItem] = useState(null);
  const [zoomSrc, setZoomSrc] = useState("");
  const openPost = useCallback((item, { compose = false } = {}) => {
    setProfileItem(null);
    setFocusComposer(compose);
    setPostItem(item);
  }, []);
  const openDiscussion = useCallback((item) => openPost(item, { compose: true }), [openPost]);
  const openProfile = useCallback((item) => setProfileItem(item), []);
  const zoomImage = useCallback((src) => setZoomSrc(src), []);

  return (
    <>
      <Box ref={containerRef} sx={{ width: "100%" }} style={containerStyle}>
        {visible.map((entry) => (
          <Box
            key={entry.key}
            ref={measureRef(entry.key)}
            style={getItemStyle(entry)}
            sx={cardShellSx}
          >
            <CommunityCard
              item={entry.item}
              onOpenComments={openDiscussion}
              onOpenPost={openPost}
              onOpenProfile={openProfile}
              onZoomImage={zoomImage}
            />
          </Box>
        ))}
      </Box>
      {hasMore && !error ? <Box ref={sentinelRef} height="1px" /> : null}
      <FeedStatus
        loading={loading}
        error={error}
        hasMore={hasMore}
        onRetry={onRetry}
        count={items.length}
      />
      <PostDialog
        item={postItem}
        onClose={() => setPostItem(null)}
        onZoomImage={zoomImage}
        onOpenProfile={openProfile}
        focusComposer={focusComposer}
      />
      <ProfileDialog
        item={profileItem}
        items={items}
        onClose={() => setProfileItem(null)}
        onOpenPost={(item) => openPost(item)}
      />
      <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc("")} />
    </>
  );
}

CommunityFeed.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  columnCount: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.any,
  hasMore: PropTypes.bool,
  sentinelRef: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
};

export default CommunityFeed;
